import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
  ArrowLeftRight,
  ChevronDown,
  HandHelping,
  Inbox,
  MapPin,
  type LucideIcon,
} from 'lucide-react';
import { MarketplaceShiftSummary, ShiftMarketplaceRequest } from '../../types';
import { trimmedMarketplaceComment } from '../../utils/shiftMarketplacePending';
import { formatCompanyDate, formatCompanyDateTime } from '../../utils/companyDateFormat';

function parseLocalDateTime(dateTimeStr: string): Date {
  if (!dateTimeStr) return new Date();
  try {
    const raw = dateTimeStr.trim().replace(/Z$/i, '');
    if (!raw.includes('+') && !raw.includes('-', 10)) {
      const [datePart, timePart] = raw.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = (timePart || '00:00:00').split(':').map(Number);
      return new Date(year, month - 1, day, hour, minute, second || 0);
    }
    return parseISO(dateTimeStr);
  } catch {
    return new Date(dateTimeStr);
  }
}

function shiftTimeOnly(start?: string, end?: string): string | undefined {
  if (!start || !end) return undefined;
  const s = parseLocalDateTime(start);
  const e = parseLocalDateTime(end);
  return `${format(s, 'HH:mm')} – ${format(e, 'HH:mm')}`;
}

function shiftDateLabel(start?: string): string | undefined {
  if (!start) return undefined;
  const s = parseLocalDateTime(start);
  return `${format(s, 'EEE')} ${formatCompanyDate(s)}`;
}

function formatShiftBlock(shift?: MarketplaceShiftSummary | null): string {
  if (!shift?.startTime || !shift?.endTime) return '—';
  const date = shiftDateLabel(shift.startTime);
  const time = shiftTimeOnly(shift.startTime, shift.endTime);
  const sched = shift.scheduleName?.trim();
  return [date, time, sched ? `"${sched}"` : null].filter(Boolean).join(' · ');
}

export function marketplaceTypeLabel(request: ShiftMarketplaceRequest): string {
  return request.requestType === 'Swap' ? 'Shift swap' : 'Shift cover';
}

/** Title from the viewer's role: proposed/accepted by me, proposed to me, or generic. */
export function marketplaceTitleForViewer(
  request: ShiftMarketplaceRequest,
  viewerEmployeeId?: number | null
): string {
  const kind = request.requestType === 'Swap' ? 'Shift Swap' : 'Shift Cover';
  if (!viewerEmployeeId) return kind;

  if (request.offeredByEmployeeId === viewerEmployeeId) {
    return `${kind} Proposed by me`;
  }

  if (request.acceptedByEmployeeId === viewerEmployeeId) {
    return `${kind} Accepted by me`;
  }

  const isSwapTarget =
    request.requestType === 'Swap' && request.requestedShift?.employeeId === viewerEmployeeId;
  if (isSwapTarget) {
    return request.status === 'Open' ? `${kind} Proposed to me` : `${kind} Accepted by me`;
  }

  return kind;
}

export type MarketplaceViewerRole = 'proposed' | 'accepted' | 'incoming' | 'other';

/** Viewer stance on this request — drives icon / chip styling. */
export function marketplaceViewerRole(
  request: ShiftMarketplaceRequest,
  viewerEmployeeId?: number | null
): MarketplaceViewerRole {
  if (!viewerEmployeeId) return 'other';
  if (request.offeredByEmployeeId === viewerEmployeeId) return 'proposed';
  if (request.acceptedByEmployeeId === viewerEmployeeId) return 'accepted';
  const isSwapTarget =
    request.requestType === 'Swap' && request.requestedShift?.employeeId === viewerEmployeeId;
  if (isSwapTarget) return request.status === 'Open' ? 'incoming' : 'accepted';
  return 'other';
}

/** Icon + colour chip: swap vs cover, and proposed vs accepted/incoming. */
export function marketplaceHeaderVisual(
  request: ShiftMarketplaceRequest,
  viewerEmployeeId?: number | null
): { Icon: LucideIcon; chipClass: string; roleLabel?: string; roleChipClass?: string } {
  const isSwap = request.requestType === 'Swap';
  const TypeIcon = isSwap ? ArrowLeftRight : HandHelping;
  const role = marketplaceViewerRole(request, viewerEmployeeId);

  if (role === 'proposed') {
    return {
      Icon: TypeIcon,
      chipClass: 'bg-violet-100 text-violet-800 ring-1 ring-violet-200',
      roleLabel: 'You proposed',
      roleChipClass: 'bg-violet-100 text-violet-800',
    };
  }
  if (role === 'accepted') {
    return {
      Icon: TypeIcon,
      chipClass: 'bg-teal-100 text-teal-800 ring-1 ring-teal-200',
      roleLabel: 'You accepted',
      roleChipClass: 'bg-teal-100 text-teal-800',
    };
  }
  if (role === 'incoming') {
    return {
      Icon: Inbox,
      chipClass: 'bg-sky-100 text-sky-800 ring-1 ring-sky-200',
      roleLabel: 'For you',
      roleChipClass: 'bg-sky-100 text-sky-800',
    };
  }
  return {
    Icon: TypeIcon,
    chipClass: 'bg-amber-100 text-amber-800',
  };
}

export function marketplaceDefaultExpanded(status: string): boolean {
  return status === 'Open' || status === 'Accepted' || status === 'PendingApproval';
}

/** Plain-language status for staff and managers. */
export function marketplaceStatusMeta(
  status: string,
  opts?: { isResponse?: boolean; requestType?: string }
): { label: string; className: string } {
  switch (status) {
    case 'Completed':
      return { label: 'Approved', className: 'bg-green-100 text-green-800' };
    case 'Rejected':
      return { label: 'Rejected', className: 'bg-red-100 text-red-800' };
    case 'PendingApproval':
    case 'Accepted':
      return { label: 'Waiting for manager', className: 'bg-amber-100 text-amber-900' };
    case 'Open':
      return {
        label: opts?.requestType === 'Swap' ? 'Waiting for colleague' : 'Open for cover',
        className: 'bg-blue-100 text-blue-800',
      };
    case 'Expired':
      return { label: 'Expired', className: 'bg-neutral-200 text-neutral-700' };
    case 'Cancelled':
      return {
        label: opts?.isResponse ? 'Declined' : 'Cancelled',
        className: 'bg-neutral-200 text-neutral-700',
      };
    default:
      return { label: status, className: 'bg-neutral-100 text-neutral-700' };
  }
}

function commentSnippets(request: ShiftMarketplaceRequest) {
  const requesterComment = trimmedMarketplaceComment(request.notes);
  const responderComment = trimmedMarketplaceComment(request.responseNotes);
  const approverComment =
    trimmedMarketplaceComment(request.approvalNotes) ||
    (request.status === 'Rejected' ? trimmedMarketplaceComment(request.rejectionReason) : undefined);
  return { requesterComment, responderComment, approverComment };
}

function scheduleNames(offered?: MarketplaceShiftSummary, requested?: MarketplaceShiftSummary) {
  const names = [offered?.scheduleName, requested?.scheduleName]
    .map((n) => n?.trim())
    .filter((n, i, all): n is string => Boolean(n) && all.indexOf(n) === i);
  return names.join(' / ') || undefined;
}

function ShiftStateRow({
  label,
  value,
  muted,
}: {
  label: string;
  value: string;
  muted?: boolean;
}) {
  return (
    <div className={`mt-1.5 ${muted ? 'rounded-md border border-dashed border-neutral-300 bg-neutral-50/80 px-2 py-1.5' : ''}`}>
      <div className={`text-[10px] font-semibold uppercase tracking-wide ${muted ? 'text-neutral-400' : 'text-neutral-500'}`}>
        {label}
      </div>
      <div className={`text-sm ${muted ? 'text-neutral-600' : 'text-neutral-800'}`}>{value}</div>
    </div>
  );
}

function PartyColumn({
  title,
  name,
  primaryShiftLabel,
  primaryShiftValue,
  secondaryShiftLabel,
  secondaryShiftValue,
  secondaryMuted,
  actionLabel,
  actionAt,
  comment,
}: {
  title: string;
  name?: string | null;
  primaryShiftLabel: string;
  primaryShiftValue: string;
  secondaryShiftLabel: string;
  secondaryShiftValue: string;
  secondaryMuted?: boolean;
  actionLabel?: string;
  actionAt?: string | null;
  comment?: string | null;
}) {
  const when = actionAt ? formatCompanyDateTime(actionAt) : '';
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">{title}</div>
      <div className="mt-1 text-sm font-semibold text-neutral-900 break-words">{name?.trim() || '—'}</div>
      {when && actionLabel && (
        <div className="text-xs text-neutral-500">
          {actionLabel} {when}
        </div>
      )}
      <ShiftStateRow label={primaryShiftLabel} value={primaryShiftValue} />
      <ShiftStateRow label={secondaryShiftLabel} value={secondaryShiftValue} muted={secondaryMuted} />
      {comment && (
        <div className="mt-1 text-xs text-neutral-600 italic line-clamp-2" title={comment}>
          {comment}
        </div>
      )}
    </div>
  );
}

/** Build Current / If approved (pending) or Before / After (approved) for each party. */

export function marketplacePartyShiftStates(request: ShiftMarketplaceRequest): {
  isApproved: boolean;
  proposer: { primaryLabel: string; primaryValue: string; secondaryLabel: string; secondaryValue: string };
  acceptor: { primaryLabel: string; primaryValue: string; secondaryLabel: string; secondaryValue: string };
} {
  const offered = request.offeredShift;
  const requested = request.requestedShift;
  const isSwap = request.requestType === 'Swap';
  const isApproved = request.status === 'Completed';
  const offeredBlock = formatShiftBlock(offered);
  const requestedBlock = formatShiftBlock(requested);

  if (isApproved) {
    return {
      isApproved: true,
      proposer: {
        primaryLabel: 'Before',
        primaryValue: offeredBlock,
        secondaryLabel: 'After',
        secondaryValue: isSwap ? requestedBlock : 'No longer on this shift',
      },
      acceptor: {
        primaryLabel: 'Before',
        primaryValue: isSwap ? requestedBlock : 'Not on this shift',
        secondaryLabel: 'After',
        secondaryValue: offeredBlock,
      },
    };
  }

  return {
    isApproved: false,
    proposer: {
      primaryLabel: 'Current',
      primaryValue: offeredBlock,
      secondaryLabel: 'If approved',
      secondaryValue: isSwap ? requestedBlock : 'No longer on this shift',
    },
    acceptor: {
      primaryLabel: 'Current',
      primaryValue: isSwap ? requestedBlock : 'Not on this shift',
      secondaryLabel: 'If approved',
      secondaryValue: offeredBlock,
    },
  };
}

export function MarketplaceRequestDetails({
  request,
  viewerEmployeeId,
  statusOverride,
  subtitle,
  footerNote,
  titleId,
  variant = 'compact',
  defaultExpanded,
  collapsible = variant !== 'detailed',
  children,
}: {
  request: ShiftMarketplaceRequest;
  /** When set, title becomes “Proposed by me” / “Accepted by me” etc. */
  viewerEmployeeId?: number | null;
  statusOverride?: { label: string; className: string };
  subtitle?: string;
  footerNote?: string;
  titleId?: string;
  variant?: 'compact' | 'detailed';
  defaultExpanded?: boolean;
  collapsible?: boolean;
  children?: React.ReactNode;
}) {
  const offered = request.offeredShift;
  const requested = request.requestedShift;
  const isSwap = request.requestType === 'Swap';
  const headerVisual = marketplaceHeaderVisual(request, viewerEmployeeId);
  const TypeIcon = headerVisual.Icon;
  const status =
    statusOverride ??
    marketplaceStatusMeta(request.status, { requestType: request.requestType });
  const { requesterComment, responderComment, approverComment } = commentSnippets(request);
  const offeredByName =
    request.offeredByName || (request.offeredByEmployeeId ? `Employee #${request.offeredByEmployeeId}` : null);
  const requestTitle = marketplaceTitleForViewer(request, viewerEmployeeId);
  const titleDate = shiftDateLabel(offered?.startTime);
  const titleSchedule = scheduleNames(offered, requested);
  const titlePosition = offered?.positionTitle?.trim();
  const acceptedAt =
    request.acceptedAt ||
    (request.acceptedByName && (request.status === 'Accepted' || request.status === 'PendingApproval')
      ? request.updatedAt
      : undefined);
  const isElapsed = request.status === 'Expired';
  const isRejected = request.status === 'Rejected';
  const isApproved = request.status === 'Completed';
  const approverName = request.approvedByName?.trim();
  const approverTitle = isElapsed
    ? 'Approved / Rejected by'
    : isRejected
      ? 'Rejected by'
      : isApproved
        ? 'Approved by'
        : 'Manager';
  const approverDisplay = isElapsed
    ? `Expired — Approver: ${approverName || '—'}`
    : isApproved || isRejected
      ? approverName
      : 'Awaiting decision';
  const approverActionLabel = isRejected ? 'Rejected' : isApproved ? 'Approved' : undefined;
  const pad = variant === 'detailed' ? 'px-4 py-3' : 'px-3 py-2.5';
  const initiallyExpanded = defaultExpanded ?? marketplaceDefaultExpanded(request.status);
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const shiftStates = marketplacePartyShiftStates(request);

  const acceptorName =
    request.acceptedByName ||
    (isSwap
      ? requested?.employeeName ||
        (requested?.employeeId ? `Employee #${requested.employeeId}` : 'Colleague')
      : request.status === 'Open'
        ? 'Anyone eligible'
        : request.acceptedByName);

  useEffect(() => {
    setExpanded(initiallyExpanded);
  }, [request.id, initiallyExpanded]);

  const open = !collapsible || expanded;

  const headerInner = (
    <>
      <div
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md mt-0.5 ${headerVisual.chipClass}`}
        title={headerVisual.roleLabel || (isSwap ? 'Shift swap' : 'Shift cover')}
      >
        <TypeIcon className="h-4 w-4" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <h3 id={titleId} className="text-sm font-semibold text-neutral-900">
                  {requestTitle}
                  {titleDate && <span className="font-medium text-neutral-700"> · {titleDate}</span>}
                  {titleSchedule && <span className="font-medium text-neutral-700"> · {titleSchedule}</span>}
                  {titlePosition && <span className="font-medium text-neutral-700"> · {titlePosition}</span>}
                </h3>
          {headerVisual.roleLabel && headerVisual.roleChipClass && (
            <span
              className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${headerVisual.roleChipClass}`}
            >
              {headerVisual.roleLabel}
            </span>
          )}
          <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${status.className}`}>
            {status.label}
          </span>
          {request.locationName && (
            <span className="inline-flex items-center gap-1 text-sm text-neutral-600">
              <MapPin className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden />
              {request.locationName}
            </span>
          )}
          {subtitle && <span className="text-sm text-neutral-500">{subtitle}</span>}
        </div>
      </div>
    </>
  );

  return (
    <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
      <div
        className={`bg-gradient-to-r from-amber-50 to-white ${pad} ${open ? 'border-b border-amber-100' : ''}`}
      >
        {collapsible ? (
          <button
            type="button"
            className="flex items-start gap-2.5 min-w-0 w-full text-left rounded-md -m-1 p-1 hover:bg-amber-50/80"
            aria-expanded={open}
            onClick={() => setExpanded((v) => !v)}
          >
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-neutral-500 mt-1.5 transition-transform ${open ? '' : '-rotate-90'}`}
              aria-hidden
            />
            {headerInner}
          </button>
        ) : (
          <div className="flex items-start gap-2.5 min-w-0">{headerInner}</div>
        )}
      </div>

      {open && (
        <>
          <div className={`${pad} grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4`}>
            <PartyColumn
              title="Proposer"
              name={offeredByName}
              primaryShiftLabel={shiftStates.proposer.primaryLabel}
              primaryShiftValue={shiftStates.proposer.primaryValue}
              secondaryShiftLabel={shiftStates.proposer.secondaryLabel}
              secondaryShiftValue={shiftStates.proposer.secondaryValue}
              secondaryMuted={!shiftStates.isApproved}
              actionLabel="Requested"
              actionAt={request.createdAt}
              comment={requesterComment}
            />
            <PartyColumn
              title={isSwap ? 'Colleague' : 'Cover taker'}
              name={acceptorName}
              primaryShiftLabel={shiftStates.acceptor.primaryLabel}
              primaryShiftValue={shiftStates.acceptor.primaryValue}
              secondaryShiftLabel={shiftStates.acceptor.secondaryLabel}
              secondaryShiftValue={shiftStates.acceptor.secondaryValue}
              secondaryMuted={!shiftStates.isApproved}
              actionLabel={acceptedAt ? 'Accepted' : undefined}
              actionAt={acceptedAt}
              comment={responderComment}
            />
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                {approverTitle}
              </div>
              <div className="mt-1 text-sm font-semibold text-neutral-900 break-words">
                {approverDisplay || '—'}
              </div>
              {approverActionLabel && request.approvedAt && (
                <div className="text-xs text-neutral-500">
                  {approverActionLabel} {formatCompanyDateTime(request.approvedAt)}
                </div>
              )}
              {approverComment && (
                <div className="mt-1 text-xs text-neutral-600 italic line-clamp-2" title={approverComment}>
                  {approverComment}
                </div>
              )}
            </div>
          </div>

          {footerNote && variant === 'detailed' && (
            <div className={`${pad} pt-0 text-xs text-neutral-500`}>{footerNote}</div>
          )}
          {children && <div className={`${pad} pt-0`}>{children}</div>}
        </>
      )}
    </div>
  );
}
